import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FileManager from "@/components/FileManager";
import { toast } from "sonner";
import {
  Search, MapPin, Clock, ShieldCheck, QrCode, Wallet, User, LogOut,
  HeartPulse, Zap, Calendar, TrendingUp, CheckCircle2, AlertCircle, Loader2
} from "lucide-react";

const specialtyBadge = (s) => (
  <Badge className="bg-sky-50 text-sky-700 border border-sky-200 rounded-full text-[10px] font-mono uppercase tracking-widest">{s}</Badge>
);

const urgencyBadge = (u) => {
  const map = {
    EMERGENCY: "bg-red-50 text-red-700 border-red-200",
    URGENT: "bg-amber-50 text-amber-700 border-amber-200",
    NORMAL: "bg-slate-50 text-slate-600 border-slate-200",
  };
  return <Badge className={`${map[u]} border rounded-full text-[10px] font-mono uppercase tracking-widest`}>{u}</Badge>;
};

function TopBar() {
  const { user, logout } = useAuth();
  return (
    <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
      <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-[#0B1528] flex items-center justify-center">
            <HeartPulse className="w-3.5 h-3.5 text-sky-400" />
          </div>
          <span className="font-extrabold text-sm">MedShift</span>
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">Doctor</Badge>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right hidden sm:block">
            <div className="text-xs font-semibold">{user?.name}</div>
            <div className="text-[10px] text-slate-500">{user?.email}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={logout} data-testid="doctor-logout-btn"><LogOut className="w-4 h-4" /></Button>
        </div>
      </div>
    </header>
  );
}

function BottomNav() {
  const tabs = [
    { to: "/doctor", label: "Duties", icon: Search, end: true, tid: "tab-duties" },
    { to: "/doctor/shifts", label: "My Shifts", icon: Calendar, tid: "tab-shifts" },
    { to: "/doctor/earnings", label: "Earnings", icon: Wallet, tid: "tab-earnings" },
    { to: "/doctor/profile", label: "Profile", icon: User, tid: "tab-profile" },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30">
      <div className="max-w-3xl mx-auto px-2 grid grid-cols-4">
        {tabs.map(t => (
          <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.tid}
            className={({isActive}) => `flex flex-col items-center py-2.5 gap-0.5 text-[11px] font-medium ${isActive ? "text-sky-600" : "text-slate-500"}`}>
            <t.icon className="w-5 h-5" /> {t.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function DutyCard({ duty, onAccept }) {
  return (
    <Card className="p-4 rounded-2xl border-slate-200 med-shadow" data-testid={`duty-card-${duty.duty_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            {specialtyBadge(duty.specialty)} {urgencyBadge(duty.urgency)}
          </div>
          <h4 className="mt-2 font-bold">{duty.hospital_name}</h4>
          <div className="text-xs text-slate-500 flex items-center gap-2 mt-1">
            <MapPin className="w-3 h-3" /> {duty.hospital_area} · {duty.distance_km} km
            <span className="text-slate-300">·</span>
            <Clock className="w-3 h-3" /> {duty.date} {duty.start_time}–{duty.end_time}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono font-bold text-lg">₹{duty.hourly_rate}</div>
          <div className="text-[10px] text-slate-500">per hour</div>
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-xl h-10" onClick={() => onAccept(duty)} data-testid={`accept-duty-btn-${duty.duty_id}`}>Accept</Button>
        <Button variant="outline" className="rounded-xl h-10" data-testid={`decline-duty-btn-${duty.duty_id}`}>Decline</Button>
      </div>
    </Card>
  );
}

function DutiesTab() {
  const [duties, setDuties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/doctor/duties"); setDuties(data); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const accept = async (duty) => {
    try {
      await api.post(`/doctor/duties/${duty.duty_id}/accept`);
      toast.success("Shift confirmed!");
      load();
    } catch { toast.error("Could not accept"); }
  };

  const filtered = duties.filter(d =>
    !q || d.hospital_name.toLowerCase().includes(q.toLowerCase()) || d.specialty.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="duties-tab">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">Available duties</h1>
        <p className="text-sm text-slate-500 mt-1">Matching your specialties in Hyderabad.</p>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search hospital or specialty…" className="pl-9 h-11 rounded-xl" data-testid="duties-search" />
      </div>
      {loading ? <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline text-sky-600" /></div>
        : filtered.length === 0 ? <Card className="p-8 text-center text-sm text-slate-500">No duties matching right now. Check back soon.</Card>
        : filtered.map(d => <DutyCard key={d.duty_id} duty={d} onAccept={accept} />)}
    </div>
  );
}

function ShiftsTab() {
  const [shifts, setShifts] = useState([]);
  const load = async () => { try { const { data } = await api.get("/doctor/my-shifts"); setShifts(data); } catch {} };
  useEffect(() => { load(); }, []);

  const checkIn = async (d) => {
    try { await api.post("/doctor/attendance/check-in", { duty_id: d.duty_id }); toast.success("Checked in!"); load(); }
    catch { toast.error("Check-in failed"); }
  };
  const checkOut = async (d) => {
    try {
      const { data } = await api.post("/doctor/attendance/check-out", { duty_id: d.duty_id });
      toast.success(`Checked out. ₹${data.amount} pending approval.`); load();
    } catch { toast.error("Check-out failed"); }
  };

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="shifts-tab">
      <h1 className="text-2xl font-extrabold tracking-tight">My shifts</h1>
      {shifts.length === 0 && <Card className="p-8 text-center text-sm text-slate-500">You haven't accepted any shifts yet.</Card>}
      {shifts.map(d => (
        <Card key={d.duty_id} className="p-4 rounded-2xl med-shadow" data-testid={`shift-${d.duty_id}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="flex items-center gap-2">{specialtyBadge(d.specialty)}<Badge className="bg-slate-100 text-slate-700 rounded-full text-[10px]">{d.status}</Badge></div>
              <h4 className="mt-1 font-bold">{d.hospital_name}</h4>
              <div className="text-xs text-slate-500 mt-1">{d.date} · {d.start_time}–{d.end_time} · ₹{d.hourly_rate}/hr</div>
            </div>
            {d.status === "ACCEPTED" && (
              <Button className="bg-sky-600 hover:bg-sky-700 rounded-xl" onClick={() => checkIn(d)} data-testid={`check-in-btn-${d.duty_id}`}>
                <QrCode className="w-4 h-4 mr-1" /> Check in
              </Button>
            )}
            {d.status === "IN_PROGRESS" && (
              <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-xl" onClick={() => checkOut(d)} data-testid={`check-out-btn-${d.duty_id}`}>
                <CheckCircle2 className="w-4 h-4 mr-1" /> Check out
              </Button>
            )}
            {d.status === "COMPLETED" && (
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                ₹{d.attendance?.amount || 0} · {d.attendance?.approved ? "Paid" : "Pending"}
              </Badge>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

function EarningsTab() {
  const [e, setE] = useState(null);
  useEffect(() => { api.get("/doctor/earnings").then(({data}) => setE(data)).catch(() => {}); }, []);
  if (!e) return <div className="p-4 pb-24"><Loader2 className="w-5 h-5 animate-spin text-sky-600" /></div>;

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="earnings-tab">
      <h1 className="text-2xl font-extrabold tracking-tight">Earnings</h1>
      <div className="grid grid-cols-2 gap-3">
        {[["Today", e.today], ["This week", e.this_week], ["Pending", e.pending], ["Lifetime", e.lifetime]].map(([l,v]) => (
          <Card key={l} className="p-4 rounded-2xl">
            <div className="text-xs text-slate-500">{l}</div>
            <div className="font-mono font-bold text-xl mt-1">₹{v || 0}</div>
          </Card>
        ))}
      </div>
      <Card className="p-4 rounded-2xl">
        <h3 className="font-bold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-sky-600" /> Payment history</h3>
        <div className="mt-3 space-y-2">
          {(e.history || []).length === 0 && <div className="text-sm text-slate-500 py-4 text-center">No payments yet.</div>}
          {(e.history || []).map((a, i) => (
            <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
              <div>
                <div className="font-medium">{a.hours_worked}h shift</div>
                <div className="text-xs text-slate-500">{(a.check_out || "").slice(0,16)}</div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold">₹{a.amount}</div>
                <Badge className={`text-[10px] ${a.approved ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"} border-transparent`}>
                  {a.approved ? "Paid" : "Pending"}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function ProfileTab() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/doctor/me").then(({data}) => setP(data)).catch(() => {}); }, []);

  if (!p) return <div className="p-4 pb-24"><Loader2 className="w-5 h-5 animate-spin text-sky-600" /></div>;

  const save = async () => {
    setSaving(true);
    try { await api.put("/doctor/me", p); toast.success("Profile updated"); }
    catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="profile-tab">
      <h1 className="text-2xl font-extrabold tracking-tight">Profile & Verification</h1>
      <Card className="p-5 rounded-2xl">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold">
            {p.full_name?.[0] || "D"}
          </div>
          <div>
            <div className="font-bold">{p.full_name}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1">
              {p.verification_status === "VERIFIED" ? (
                <><ShieldCheck className="w-3 h-3 text-emerald-500" /> TGMC Verified</>
              ) : (
                <><AlertCircle className="w-3 h-3 text-amber-500" /> {p.verification_status}</>
              )}
            </div>
          </div>
        </div>
      </Card>
      <Card className="p-5 rounded-2xl space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Mobile</Label><Input value={p.mobile || ""} onChange={e => setP({...p, mobile: e.target.value})} data-testid="profile-mobile" /></div>
          <div><Label>Locality</Label><Input value={p.locality || ""} onChange={e => setP({...p, locality: e.target.value})} data-testid="profile-locality" /></div>
          <div><Label>MBBS</Label><Input value={p.mbbs || ""} onChange={e => setP({...p, mbbs: e.target.value})} /></div>
          <div><Label>PG Qualification</Label><Input value={p.pg_qualification || ""} onChange={e => setP({...p, pg_qualification: e.target.value})} /></div>
          <div><Label>Experience (years)</Label><Input type="number" value={p.experience_years || 0} onChange={e => setP({...p, experience_years: parseFloat(e.target.value)})} /></div>
          <div><Label>Hourly rate (₹)</Label><Input type="number" value={p.hourly_rate || 0} onChange={e => setP({...p, hourly_rate: parseFloat(e.target.value)})} data-testid="profile-rate" /></div>
        </div>
        <div>
          <Label>TGMC Registration No.</Label>
          <Input value={p.medical_registration_no || ""} onChange={e => setP({...p, medical_registration_no: e.target.value})} />
        </div>
        <div>
          <Label>Specialties (comma separated)</Label>
          <Input value={(p.specialties || []).join(", ")} onChange={e => setP({...p, specialties: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})} data-testid="profile-specialties" />
        </div>
        <div className="flex items-center justify-between pt-2">
          <div>
            <Label>Emergency available</Label>
            <div className="text-xs text-slate-500">Receive urgent broadcasts</div>
          </div>
          <Switch checked={p.emergency_available} onCheckedChange={v => setP({...p, emergency_available: v})} />
        </div>
        <Button onClick={save} disabled={saving} className="w-full bg-sky-600 hover:bg-sky-700 rounded-xl h-11" data-testid="save-profile-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save profile"}
        </Button>
      </Card>
      <FileManager docType="credential" title="Credential locker (uploads)" testid="doctor-file-manager" />
      <Card className="p-5 rounded-2xl">
        <h3 className="font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Credential locker</h3>
        <div className="mt-3 space-y-2">
          {(p.documents || []).map((d, i) => (
            <div key={i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
              <div>
                <div className="font-medium">{d.type}</div>
                <div className="text-xs text-slate-500">{d.name}</div>
              </div>
              {d.verified ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Verified</Badge>
                : <Badge className="bg-amber-50 text-amber-700 border border-amber-200">Pending</Badge>}
            </div>
          ))}
          {(p.documents || []).length === 0 && <div className="text-sm text-slate-500 py-2">No documents uploaded.</div>}
        </div>
      </Card>
    </div>
  );
}

export default function DoctorApp() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/");
    else if (user.role && user.role !== "doctor" && user.role !== "admin") navigate("/choose-role");
  }, [user, loading, navigate]);

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar />
      <main className="max-w-3xl mx-auto">
        <Routes>
          <Route index element={<DutiesTab />} />
          <Route path="shifts" element={<ShiftsTab />} />
          <Route path="earnings" element={<EarningsTab />} />
          <Route path="profile" element={<ProfileTab />} />
        </Routes>
      </main>
      <BottomNav />
    </div>
  );
}
